// src/pages/admin/templates/TemplatesList.tsx
import { PlusIcon } from '@heroicons/react/16/solid';
import { ClockIcon } from '@heroicons/react/24/solid';
import { useEffect, useMemo, useState } from 'react';
import {
  collection,
  doc as fsDoc,
  getDoc,
  getDocs,
  getCountFromServer,
  orderBy,
  limit as qsLimit,
  query,
  startAfter,
  Timestamp,
  QueryDocumentSnapshot,
  DocumentData,
} from 'firebase/firestore';
import { db } from '../../../config/firebaseConfig';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';

type Status = 'draft' | 'published' | 'archived';
type Version = 'draft' | 'published';

type VersionRow = { kind: Version; ts?: Timestamp | null };
type Row = {
  id: string;
  title: string;
  short: string;
  versions: VersionRow[];
  archived: boolean;
  latestTs?: Timestamp | null;
  cardBgUrl: string
};

const PAGE_SIZE = 10;
const MAX_PAGE_BTNS = 5;

const chipClasses: Record<Status, string> = {
  draft: 'bg-amber-50 text-amber-600 border border-amber-200',
  published: 'bg-emerald-50 text-emerald-800 border border-emerald-200',
  archived: 'bg-zinc-100 text-zinc-700 border border-zinc-200',
};

const firstTs = (o: any): Timestamp | null => {
  if (!o || typeof o !== 'object') return null;
  return o.publishedAt || o.updatedAt || o.createdAt || null;
};

const fmt = (ts?: Timestamp | any) => {
  if (!ts) return '—';
  const d = ts instanceof Timestamp ? ts.toDate() : new Date(ts!.seconds * 1000);
  const day = String(d.getDate()).padStart(2, '0');
  const month = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][d.getMonth()];
  const year = d.getFullYear();
  let h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, '0');
  const ampm = h >= 12 ? 'pm' : 'am';
  h = h % 12 || 12;
  return `${day}-${month}-${year}, ${h}:${m} ${ampm}`;
};

export default function TemplatesList() {
  const nav = useNavigate();
  const { user } = useSelector((s: RootState) => s.auth);

  // data for the current page
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);

  // filters (applied client-side on the current page)
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | Status>('all');

  // pagination state
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [imgLoaded, setImgLoaded] = useState(false);
const [imgError, setImgError] = useState(false);

  // store last doc (cursor) for each page (index = pageNumber)
  const [cursors, setCursors] = useState<Record<number, QueryDocumentSnapshot<DocumentData> | null>>({});

  // ----- helpers -----
  const buildRowsFromSnap = async (snapDocs: QueryDocumentSnapshot<DocumentData>[]) => {
    const built = await Promise.all(
      snapDocs.map(async (d) => {
        const parent = (d.data() as any) || {};

        const draftRef = fsDoc(d.ref, 'versions', 'draft');
        const pubRef   = fsDoc(d.ref, 'versions', 'published');
        const [ds, ps] = await Promise.all([getDoc(draftRef), getDoc(pubRef)]);
        const draft = ds.exists() ? ds.data() : null;
        const published = ps.exists() ? ps.data() : null;
        const archived = !!parent.archived || parent.status === 'archived';

        const versions: VersionRow[] = [];
        if (draft)     versions.push({ kind: 'draft',     ts: firstTs(draft) });
        if (published) versions.push({ kind: 'published', ts: firstTs(published) });

        const title: string =
          (draft?.title as string) ??
          (published?.title as string) ??
          (parent.title as string) ??
          '(untitled)';

        const short: string =
          (draft?.shortDesc as string) ??
          (draft?.short as string) ??
          (published?.shortDesc as string) ??
          (published?.short as string) ??
          (parent.shortDesc as string) ??
          (parent.short as string) ??
          '';

        const latestTs =
          [firstTs(draft), firstTs(published)]
            .filter(Boolean)
            .sort((a: any, b: any) => (b!.seconds ?? 0) - (a!.seconds ?? 0))[0] ?? null;

        console.log(parent)
        const cardBgUrl = draft?.cardBgUrl || published?.cardBgUrl || null

        return { id: d.id, title, short, versions, archived, latestTs, cardBgUrl } as Row;
      })
    );
    return built;
  };

  const queryForPage = async (targetPage: number) => {
    // find the nearest known cursor before the target
    let startPage = 1;
    let anchor: QueryDocumentSnapshot<DocumentData> | null = null;
    for (let p = targetPage - 1; p >= 1; p--) {
      if (cursors[p]) {
        startPage = p + 1;
        anchor = cursors[p]!;
        break;
      }
    }

    // walk forward until we reach targetPage
    let curAnchor = anchor;
    let lastDocs: QueryDocumentSnapshot<DocumentData>[] = [];
    for (let p = startPage; p <= targetPage; p++) {
      const baseCol = collection(db, 'templates', 'meta', 'data');
      const qRef = curAnchor
        ? query(baseCol, orderBy('updatedAt', 'desc'), startAfter(curAnchor), qsLimit(PAGE_SIZE))
        : query(baseCol, orderBy('updatedAt', 'desc'), qsLimit(PAGE_SIZE));

      const snap = await getDocs(qRef);
      const docs = snap.docs;
      lastDocs = docs;

      // remember cursor for this page (its last doc)
      if (docs.length) {
        setCursors(prev => ({ ...prev, [p]: docs[docs.length - 1] }));
        curAnchor = docs[docs.length - 1];
      } else {
        // no more docs -> keep cursor but break
        setCursors(prev => ({ ...prev, [p]: prev[p] ?? null }));
        curAnchor = null;
      }
    }
    return lastDocs;
  };

  const loadPage = async (pageNumber: number) => {
    setLoading(true);
    try {
      // fetch total count only once
      if (pageNumber === 1 && totalPages === 1) {
        const countSnap = await getCountFromServer(collection(db, 'templates', 'meta', 'data'));
        const total = countSnap.data().count || 0;
        setTotalPages(Math.max(1, Math.ceil(total / PAGE_SIZE)));
      }

      const docs = await queryForPage(pageNumber);
      const built = await buildRowsFromSnap(docs);
      setRows(built);
      setPage(pageNumber);
    } finally {
      setLoading(false);
    }
  };

  // initial
  useEffect(() => {
    loadPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // numbers to show (windowed)
  const pageNumbers = useMemo(() => {
    const half = Math.floor(MAX_PAGE_BTNS / 2);
    let start = Math.max(1, page - half);
    let end = Math.min(totalPages, start + MAX_PAGE_BTNS - 1);
    start = Math.max(1, end - MAX_PAGE_BTNS + 1);

    const nums: number[] = [];
    for (let i = start; i <= end; i++) nums.push(i);
    return nums;
  }, [page, totalPages]);

  // filtered view (client-side on current page)
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows
      .map((r) => {
        const matchesSearch =
          !q ||
          r.title.toLowerCase().includes(q) ||
          (r.short || '').toLowerCase().includes(q);
        if (!matchesSearch) return null;

        const visibleVersions =
          filter === 'all'
            ? r.versions
            : filter === 'archived'
            ? (r.archived ? r.versions : [])
            : r.versions.filter((v) => v.kind === filter);

        if (visibleVersions.length === 0) return null;
        return { ...r, versions: visibleVersions };
      })
      .filter(Boolean) as Row[];
  }, [rows, search, filter]);

  const goView = (id: string, ver: Version) => nav(`/admin/templates/${id}?ver=${ver}`);
  const goTryItOut = () => nav(`/admin/tryout`);
  

  return (
    <div className="mx-8 my-6">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xl font-semibold text-zinc-800">Templates</h2>
        {(user?.role === "admin" || user?.role === "approver") && (
        <button
          onClick={() => nav('/admin/templates/new')}
          className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-white hover:bg-brand-600/90"
        >
          <PlusIcon className="h-4 w-4" />
          Create template
        </button>
        )}
      </div>

      {/* Search + filters */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by report title…"
          className="field-grey w-full max-w-md"
        />
        <div className="flex flex-wrap gap-2">
          {(['all', 'published', 'draft', 'archived'] as const).map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={[
                'px-3 py-1 rounded-full text-sm border',
                filter === s
                  ? 'bg-brand-600 text-white border-brand-600'
                  : 'bg-zinc-100 text-zinc-700 border-zinc-300',
              ].join(' ')}
            >
              {s === 'all' ? 'All' : s[0].toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Cards */}
      <div className="space-y-4">
        {loading && (
          <div className="rounded-xl border border-zinc-200 p-8 text-center text-zinc-400">
            Loading…
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <div className="rounded-xl border border-zinc-200 p-8 text-center text-zinc-400">
            No templates
          </div>
        )}

        {!loading &&
          filtered.map((r) => (
            <div key={r.id} className="rounded-xl border border-zinc-200">
              {/* Card header */}
              <div className="px-4 py-3 flex gap-4">
                <div>
                
                <CardImage url={r.cardBgUrl} />

                </div>
                <div>
                  <div className="font-medium text-zinc-900 truncate max-w-[50ch]">{r.title}</div>
                  {r.short && (
                    <div className="mt-0.5 line-clamp-1 text-xs text-zinc-500 truncate max-w-[100ch]">{r.short}</div>
                  )}
                </div>
                
              </div>

              {/* Version rows */}
              <div className="divide-y p-4">
                {r.versions.map((v, idx) => (
                  <div
                    key={v.kind + idx}
                    className="mb-3 flex items-center justify-between rounded-xl bg-zinc-100 px-4 py-3"
                  >
                    <div className="flex items-center gap-3">
                      <span
                        className={[
                          'inline-flex items-center gap-2 rounded px-2 py-0.5 text-xs font-medium',
                          chipClasses[v.kind],
                        ].join(' ')}
                      >
                        {v.kind}
                      </span>
                      <span className="inline-flex items-center gap-1 rounded-lg border border-zinc-300 px-2 py-1 text-xs text-zinc-500">
                        <ClockIcon className="h-3.5 w-3.5" />
                        {fmt(v.ts)}
                      </span>
                    </div>

                    <div className="flex items-center gap-10 px-12">
                      <button
                        onClick={() => goView(r.id, v.kind)}
                        className="text-sm text-zinc-700 hover:bg-zinc-100"
                      >
                        View
                      </button>
                      {(user?.role === "admin" || user?.role === "approver") && (
                      <button onClick={() =>goTryItOut() } className="text-sm text-zinc-700 hover:bg-zinc-100">
                        Try it out
                      </button>
                      )}
                      {/* {(user?.role === "admin" || user?.role === "approver") && (
                      <button className="text-sm text-zinc-700 hover:bg-zinc-100">
                        Archive
                      </button>
                      )} */}
                    </div>
                  </div>
                ))}
              </div>

              {r.archived && (
                <div className="px-4 pb-3">
                  <span
                    className={[
                      'rounded px-2 py-0.5 text-xs font-medium',
                      chipClasses.archived,
                    ].join(' ')}
                  >
                    archived
                  </span>
                </div>
              )}
            </div>
          ))}
      </div>

      {/* Pagination like your design */}
      <div className="mt-6 flex items-center justify-center gap-5 mr-8">
        <button
          onClick={() => loadPage(Math.max(1, page - 1))}
          disabled={page === 1 || loading}
          className="inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm text-zinc-700 disabled:opacity-50 hover:bg-zinc-100"
        >
          ← Previous
        </button>

        <div className="flex items-center gap-2">
          {pageNumbers.map((n) => (
            <button
              key={n}
              onClick={() => loadPage(n)}
              disabled={loading}
              className={[
                'h-8 w-8 rounded-md text-sm',
                n === page
                  ? 'bg-zinc-900 text-white'
                  : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200',
              ].join(' ')}
            >
              {n}
            </button>
          ))}
        </div>

        <button
          onClick={() => loadPage(Math.min(totalPages, page + 1))}
          disabled={page === totalPages || loading}
          className="inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm text-zinc-700 disabled:opacity-50 hover:bg-zinc-100"
        >
          Next →
        </button>
      </div>
    </div>
  );
}



function CardImage({ url }: { url: string }) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  return (
    <div className="relative w-[50px] h-[60px] overflow-hidden rounded-md">
      {/* Gradient fallback */}
      <div
        className={`absolute inset-0 transition-opacity duration-500
          ${loaded && !error ? "opacity-0" : "opacity-100"}`}
        style={{
          background: "linear-gradient(180deg, #FFFFFF 0%, #6F148D 100%)"
        }}
      />

      {/* Image */}
      <img
        src={url}
        onLoad={() => setLoaded(true)}
        onError={() => {
          setError(true);
          setLoaded(false);
        }}
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500
          ${loaded && !error ? "opacity-100" : "opacity-0"}`}
        alt=""
      />
    </div>
  );
}
